/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Handle product option dropdowns on collection pages
document.addEventListener('DOMContentLoaded', function() {
  
  // Function to update product URLs based on selected options
  function updateProductURLs(productItem) {
    const dropdowns = productItem.querySelectorAll('.product-item-select');
    const colorSwatch = productItem.querySelector('.product-item-meta__swatch-list .color-swatch__radio:checked');
    
    // Build URL parameters object
    const urlParams = {};
    
    // Add color option if exists
    if (colorSwatch) {
      const colorName = colorSwatch.name.split('-').pop(); // Get the last part which is product ID
      const optionName = colorSwatch.closest('.product-item-meta__swatch-list').getAttribute('data-option-name');
      if (optionName) {
        urlParams[optionName] = colorSwatch.value;
      }
    }
    
    // Add dropdown options
    dropdowns.forEach(dropdown => {
      const optionName = dropdown.getAttribute('data-option-name');
      if (optionName && dropdown.value) {
        urlParams[optionName] = dropdown.value;
      }
    });
    
    // Update product links with selected options
    const productLinks = productItem.querySelectorAll('a[href*="/products"]');
    productLinks.forEach(link => {
      const url = new URL(link.href);
      
      // Clear existing option parameters
      const keysToDelete = [];
      for (const key of url.searchParams.keys()) {
        if (key.toLowerCase() !== 'variant') {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach(key => url.searchParams.delete(key));
      
      // Add selected options to URL
      Object.keys(urlParams).forEach(key => {
        url.searchParams.set(key, urlParams[key]);
      });
      
      link.href = url.toString();
    });
    
    // Update quick buy buttons
    const quickBuyElements = productItem.querySelectorAll('[id*="product-"][id*="drawer"], [id*="product-"][id*="popover"]');
    quickBuyElements.forEach(element => {
      if (element.hasAttribute('href')) {
        const url = new URL(element.getAttribute('href'), window.location.origin);
        
        // Clear existing option parameters
        const keysToDelete = [];
        for (const key of url.searchParams.keys()) {
          if (key.toLowerCase() !== 'variant') {
            keysToDelete.push(key);
          }
        }
        keysToDelete.forEach(key => url.searchParams.delete(key));
        
        // Add selected options
        Object.keys(urlParams).forEach(key => {
          url.searchParams.set(key, urlParams[key]);
        });
        
        element.setAttribute('href', url.pathname + url.search);
      }
    });
  }
  
  // Function to get selected variant based on current options
  function getSelectedVariant(productItem) {
    // Get all selected option values
    const selectedOptions = {};
    
    // Get color swatch selection
    const colorSwatch = productItem.querySelector('.product-item-meta__swatch-list .color-swatch__radio:checked');
    if (colorSwatch) {
      const position = colorSwatch.getAttribute('data-option-position');
      selectedOptions['option' + position] = colorSwatch.value;
      console.log(`Option ${position}:`, colorSwatch.value);
    }
    
    // Get dropdown selections
    const dropdowns = productItem.querySelectorAll('.product-item-select');
    dropdowns.forEach(dropdown => {
      const position = dropdown.getAttribute('data-option-position');
      const valueId = dropdown.value; // This is value.id
      if (position && valueId) {
        selectedOptions['option' + position] = valueId;
        console.log(`Option ${position}:`, valueId);
      }
    });
    
    // Get product URL to fetch variant data
    const productLink = productItem.querySelector('a[href*="/products"]');
    if (!productLink) {
      console.log('✗ Product link not found');
      return null;
    }
    
    const productUrl = new URL(productLink.href);
    const productHandle = productUrl.pathname.split('/products/')[1]?.split('?')[0];
    
    if (!productHandle) {
      return null;
    }
    
    console.log('Selected options:', selectedOptions);
    
    // Fetch product data to find matching variant
    fetch(`/products/${productHandle}.js`)
      .then(response => response.json())
      .then(product => {
        // Find variant that matches all selected options
        const matchingVariant = product.variants.find(variant => {
          let matches = true;
          
          // Check each selected option
          for (let i = 1; i <= 3; i++) {
            const optionKey = 'option' + i;
            if (selectedOptions[optionKey]) {
              // Compare variant option with selected option
              if (variant[optionKey] !== selectedOptions[optionKey]) {
                matches = false;
                break;
              }
            }
          }
          
          return matches;
        });
        
        if (matchingVariant) {
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━');
          console.log('Variant ID:', matchingVariant.id);
          console.log('Variant URL:', matchingVariant.url);
          console.log('Title:', matchingVariant.title);
          console.log('Price:', matchingVariant.price / 100, product.currency || 'USD');
          console.log('Available:', matchingVariant.available);
          console.log('SKU:', matchingVariant.sku || 'N/A');
          console.log('Option1:', matchingVariant.option1);
          console.log('Option2:', matchingVariant.option2);
          console.log('Option3:', matchingVariant.option3);
          console.log('Featured Image:', matchingVariant.featured_image?.src || 'None');
          
          // Construct variant URL
          const variantUrl = `/products/${productHandle}?variant=${matchingVariant.id}`;
          console.log('Variant URL:', variantUrl);
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
          
          // Update quick-buy drawer and popover hrefs
          updateQuickBuyHrefs(productItem, variantUrl);
          
          // Update product card SKU
          updateProductCardSKU(productItem, matchingVariant);
          
          // Update product card image if variant has a featured image
          updateProductCardImage(productItem, matchingVariant);
          
          // Update product card price
          updateProductCardPrice(productItem, matchingVariant, product);
          
          return matchingVariant;
        } else {
          console.log('✗ No matching variant found for selected options');
          product.variants.map(v => ({
            id: v.id,
            options: [v.option1, v.option2, v.option3].filter(Boolean)
          }));
          return null;
        }
      })
      .catch(error => {
        console.error('Error fetching product:', error);
      });
  }
  
  // Function to update quick-buy drawer and popover hrefs
  function updateQuickBuyHrefs(productItem, variantUrl) {
    // Find quick-buy drawer and popover elements
    const quickBuyElements = productItem.querySelectorAll('[id*="product-"][id*="drawer"], [id*="product-"][id*="popover"]');
    
    quickBuyElements.forEach((element, index) => {
      if (element.hasAttribute('href')) {
        const oldHref = element.getAttribute('href');
        element.setAttribute('href', variantUrl);
      }
    });
  }
  
  // Function to update product card SKU based on selected variant
  function updateProductCardSKU(productItem, variant) {
    console.log('=== Updating product card SKU ===');
    
    // Find the SKU value element
    const skuValueElement = productItem.querySelector('.card-sku-value');
    
    if (!skuValueElement) {
      console.log('✗ SKU element not found');
      return;
    }
    
    // Update SKU if variant has one
    if (variant.sku) {
      skuValueElement.textContent = variant.sku;
      console.log('✓ SKU updated to:', variant.sku);
      
      // Make sure the SKU container is visible
      const skuContainer = productItem.querySelector('.card__sku');
      if (skuContainer) {
        skuContainer.style.display = '';
      }
    } else {
      // If no SKU, optionally hide the SKU container or show "N/A"
      skuValueElement.textContent = 'N/A';
      console.log('⚠ Variant has no SKU, showing N/A');
    }
    
    console.log('✓ SKU update complete\n');
  }
  
  // Function to update product card image based on selected variant
  function updateProductCardImage(productItem, variant) {
    console.log('=== Updating product card image ===');
    
    // Check if variant has a featured image
    if (!variant.featured_image || !variant.featured_image.src) {
      console.log('✗ Variant has no featured image, skipping image update');
      return;
    }
    
    const variantImageSrc = variant.featured_image.src;
    const variantMediaId = variant.featured_image.id;
    console.log('Variant featured image URL:', variantImageSrc);
    console.log('Variant featured image ID:', variantMediaId);
    
    // Find the product link to get aspect ratio container
    const productLink = productItem.querySelector('.product-item__aspect-ratio');
    if (!productLink) {
      console.log('✗ Product link container not found');
      return;
    }
    
    // Find all primary images in the product card
    const allPrimaryImages = productLink.querySelectorAll('.product-item__primary-image');
    console.log('Found primary images:', allPrimaryImages.length);
    
    let imageFound = false;
    
    // Hide all primary images and show the one matching the variant
    allPrimaryImages.forEach((img, index) => {
      const imgMediaId = parseInt(img.getAttribute('data-media-id'));
      
      if (imgMediaId === variantMediaId) {
        // Show this image
        img.removeAttribute('hidden');
        img.hidden = false;
        console.log(`✓ Showing image ${index} (media ID: ${imgMediaId})`);
        imageFound = true;
      } else {
        // Hide other images
        img.setAttribute('hidden', '');
        img.hidden = true;
      }
    });
    
    if (imageFound) {
      console.log('✓ Product card image updated successfully\n');
    } else {
      console.log('⚠ Image not preloaded in DOM, creating new image element');
      
      // If image not found, create and insert a new image element
      // First, hide all existing primary images
      allPrimaryImages.forEach(img => {
        img.setAttribute('hidden', '');
        img.hidden = true;
      });
      
      // Check if we already created this image before
      let dynamicImage = productLink.querySelector(`[data-media-id="${variantMediaId}"]`);
      
      if (!dynamicImage) {
        // Create new image element
        dynamicImage = document.createElement('img');
        dynamicImage.className = 'product-item__primary-image';
        dynamicImage.setAttribute('data-media-id', variantMediaId);
        dynamicImage.setAttribute('loading', 'eager');
        
        // Build srcset for responsive images
        const widths = [200, 300, 400, 500, 600, 700, 800, 900, 1000, 1100, 1200];
        const srcset = widths.map(width => {
          // Extract base URL and file extension
          const urlParts = variantImageSrc.match(/^(.+)\.([a-z]+)(\?.*)?$/i);
          if (urlParts) {
            const baseUrl = urlParts[1];
            const extension = urlParts[2];
            const queryString = urlParts[3] || '';
            return `${baseUrl}_${width}x.${extension}${queryString} ${width}w`;
          }
          return `${variantImageSrc} ${width}w`;
        }).join(', ');
        
        dynamicImage.setAttribute('srcset', srcset);
        dynamicImage.setAttribute('sizes', '(max-width: 740px) calc(100vw - 48px), (max-width: 999px) calc(50vw - 48px), calc(100vw / 4 - 48px)');
        dynamicImage.src = variantImageSrc;
        dynamicImage.alt = variant.name || variant.title || '';
        
        // Insert before the secondary image if it exists, otherwise append
        const secondaryImage = productLink.querySelector('.product-item__secondary-image');
        if (secondaryImage) {
          productLink.insertBefore(dynamicImage, secondaryImage);
        } else {
          productLink.appendChild(dynamicImage);
        }
        
        console.log('✓ New image element created and inserted');
      } else {
        // Image was previously created, just show it
        dynamicImage.removeAttribute('hidden');
        dynamicImage.hidden = false;
        console.log('✓ Previously created image element shown');
      }
      
      console.log('✓ Product card image updated successfully\n');
    }
  }
  
  // Function to format price in Shopify money format
  function formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '${{amount}}';
    
    function defaultOption(opt, def) {
      return (typeof opt === 'undefined') ? def : opt;
    }
    
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');
      
      if (isNaN(number) || number == null) {
        return 0;
      }
      
      number = (number / 100.0).toFixed(precision);
      
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const cents = parts[1] ? (decimal + parts[1]) : '';
      
      return dollars + cents;
    }
    
    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }
    
    return formatString.replace(placeholderRegex, value);
  }
  
  // Function to update product card price based on selected variant
  function updateProductCardPrice(productItem, variant, product) {
    console.log('=== Updating product card price ===');
    
    // Find the price list container
    const priceListContainer = productItem.querySelector('.product-item-meta__price-list-container');
    if (!priceListContainer) {
      console.log('✗ Price list container not found');
      return;
    }
    
    const priceList = priceListContainer.querySelector('.price-list');
    if (!priceList) {
      console.log('✗ Price list element not found');
      return;
    }
    
    // Get money format from Shopify theme settings
    const moneyFormat = window.themeVariables?.settings?.moneyFormat || '${{amount}}';
    const moneyWithCurrencyFormat = window.themeVariables?.settings?.moneyWithCurrencyFormat || '${{amount}} USD';
    const currencyCodeEnabled = window.themeVariables?.settings?.currencyCodeEnabled || false;
    
    // Clear existing price HTML
    priceList.innerHTML = '';
    
    // Build new price HTML based on variant
    let priceHTML = '';
    
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      // Sale price
      const salePrice = currencyCodeEnabled 
        ? formatMoney(variant.price, moneyWithCurrencyFormat)
        : formatMoney(variant.price, moneyFormat);
      const comparePrice = currencyCodeEnabled
        ? formatMoney(variant.compare_at_price, moneyWithCurrencyFormat)
        : formatMoney(variant.compare_at_price, moneyFormat);
      
      priceHTML = `
        <span class="price price--highlight">
          <span class="visually-hidden">Sale price</span>
          ${salePrice}
        </span>
        <span class="price price--compare">
          <span class="visually-hidden">Regular price</span>
          ${comparePrice}
        </span>
      `;
    } else {
      // Regular price
      const regularPrice = currencyCodeEnabled
        ? formatMoney(variant.price, moneyWithCurrencyFormat)
        : formatMoney(variant.price, moneyFormat);
      
      priceHTML = `
        <span class="price">
          <span class="visually-hidden">Price</span>
          ${regularPrice}
        </span>
      `;
    }
    
    // Add unit price if available
    if (variant.unit_price_measurement) {
      const unitPrice = formatMoney(variant.unit_price, moneyFormat);
      let referenceValue = '';
      if (variant.unit_price_measurement.reference_value != 1) {
        referenceValue = `<span class="unit-price-measurement__reference-value">${variant.unit_price_measurement.reference_value}</span>`;
      }
      
      priceHTML += `
        <div class="price price--block text--xsmall text--subdued">
          <div class="unit-price-measurement">
            <span class="unit-price-measurement__price">${unitPrice}</span>
            <span class="unit-price-measurement__separator">/</span>
            ${referenceValue}
            <span class="unit-price-measurement__reference-unit">${variant.unit_price_measurement.reference_unit}</span>
          </div>
        </div>
      `;
    }
    
    priceList.innerHTML = priceHTML;
    console.log('✓ Price updated to:', variant.price / 100);
    
    // Update discount badge
    updateDiscountBadge(productItem, variant);
    
    console.log('✓ Product card price updated successfully\n');
  }
  
  // Function to update discount badge
  function updateDiscountBadge(productItem, variant) {
    console.log('=== Updating discount badge ===');
    
    // Find the label list
    const labelList = productItem.querySelector('.product-item__label-list');
    if (!labelList) {
      console.log('✗ Label list not found');
      return;
    }
    
    // Find existing discount badge
    let discountBadge = labelList.querySelector('.pro-compare-price-badge');
    
    // Check if variant has a discount
    if (variant.compare_at_price && variant.compare_at_price > variant.price && variant.available) {
      const discountMode = window.themeVariables?.settings?.discountMode || 'percentage';
      let savings = '';
      
      if (discountMode === 'percentage') {
        const savingsPercent = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
        savings = savingsPercent + '%';
      } else {
        const savingsAmount = variant.compare_at_price - variant.price;
        savings = formatMoney(savingsAmount, window.themeVariables?.settings?.moneyFormat || '${{amount}}');
      }
      
      if (discountBadge) {
        // Update existing badge
        discountBadge.textContent = `Save ${savings}`;
      } else {
        // Create new badge
        discountBadge = document.createElement('span');
        discountBadge.className = 'label label--highlight pro-compare-price-badge';
        discountBadge.textContent = `Save ${savings}`;
        labelList.appendChild(discountBadge);
      }
      
      console.log('✓ Discount badge updated:', savings);
    } else {
      // Remove discount badge if no discount
      if (discountBadge) {
        discountBadge.remove();
        console.log('✓ Discount badge removed (no discount)');
      }
    }
    
    console.log('✓ Discount badge update complete\n');
  }
  
  // Handle dropdown changes
  document.addEventListener('change', function(event) {
    if (event.target.classList.contains('product-item-select')) {
      console.log('\n=== Product card dropdown changed ===');
      const productItem = event.target.closest('product-item');
      if (productItem) {
        updateProductURLs(productItem);
        
        // Get and output the selected variant
        getSelectedVariant(productItem);
      }
    }
  });
  
  // Also update URLs when color swatches change
  document.addEventListener('change', function(event) {
    if (event.target.classList.contains('color-swatch__radio')) {
      const productItem = event.target.closest('product-item');
      if (productItem) {
        // Small delay to ensure color swatch logic completes first
        setTimeout(() => {
          updateProductURLs(productItem);
          
          // Get and output the selected variant
          getSelectedVariant(productItem);
        }, 50);
      }
    }
  });
});



// Cache for variant inventory data
const variantInventoryCache = {};

// Function to get inventory data for a form
function getVariantInventory(form) {
  // Try to get cached data first
  const formId = form.id;
  if (variantInventoryCache[formId]) {
    return variantInventoryCache[formId];
  }
  
  // Find the inventory script - check multiple locations
  let inventoryScript = null;
  
  // 1. Inside the form
  inventoryScript = form.querySelector('script[data-variant-inventory]');
  
  // 2. As a sibling element (for quick-add forms)
  if (!inventoryScript) {
    inventoryScript = form.previousElementSibling;
    if (inventoryScript && inventoryScript.getAttribute('data-variant-inventory') === null) {
      inventoryScript = null;
    }
  }
  
  // 3. In parent container (for product pages)
  if (!inventoryScript) {
    const container = form.closest('.product-form') || form.parentElement;
    if (container) {
      inventoryScript = container.querySelector('script[data-variant-inventory]');
    }
  }
  
  if (inventoryScript) {
    try {
      const inventoryData = JSON.parse(inventoryScript.textContent);
      variantInventoryCache[formId] = inventoryData;
      console.log('Loaded variant inventory data:', inventoryData);
      return inventoryData;
    } catch (error) {
      console.error('Error parsing variant inventory data:', error);
    }
  }
  
  console.warn('No inventory script found for form:', formId);
  return null;
}

// Function to update backorder flag for a given form and variant ID
function updateBackorderFlag(form, variantId) {
  try {
    if (!form) {
      console.warn('Backorder flag: No form provided');
      return;
    }
    
    // Find the backorder_flag input in the form
    const backorderFlagInput = form.querySelector('input[name="properties[_backorder_flag]"]');
    
    if (!backorderFlagInput) {
      console.warn('Backorder flag: Could not find backorder_flag input in form');
      return;
    }
    
    // Get the product's backorder metafield status from the form data attribute
    const hasBackorderEnabled = form.dataset.backorderEnabled === 'true';
    
    // Get inventory data
    const inventoryData = getVariantInventory(form);
    
    if (!inventoryData) {
      console.warn('Backorder flag: No inventory data available');
      return;
    }
    
    // Get inventory quantity for this specific variant
    const inventoryQuantity = inventoryData[variantId];
    
    console.log('Updating backorder flag:', {
      variantId: variantId,
      hasBackorderEnabled: hasBackorderEnabled,
      inventoryQuantity: inventoryQuantity
    });
    
    // Update the backorder_flag value based on inventory quantity
    if (hasBackorderEnabled && inventoryQuantity !== undefined && inventoryQuantity <= 0) {
      backorderFlagInput.value = 'true';
      console.log('✓ Backorder flag set to TRUE');
    } else {
      backorderFlagInput.value = 'false';
      console.log('✓ Backorder flag set to FALSE');
    }
  } catch (error) {
    console.error('Error updating backorder flag:', error);
  }
}

// Listen for variant changes
document.addEventListener('variant:changed', function(event) {
  const variant = event.detail.variant;
  
  // The form might be the target or we need to find it in the DOM
  let form = event.target;
  
  // If target is not a form, try to find the closest form
  if (!form || form.tagName !== 'FORM') {
    form = event.target.closest('form[is="product-form"]');
  }
  
  if (!form || !variant || !variant.id) {
    console.warn('Backorder flag: Missing form or variant data');
    return;
  }
  
  // Update backorder flag using variant ID
  updateBackorderFlag(form, variant.id);
});

// Initialize backorder flag on page load
document.addEventListener('DOMContentLoaded', function() {
  console.log('Initializing backorder flags on page load...');
  
  // Find all product forms
  const productForms = document.querySelectorAll('form[is="product-form"]');
  
  productForms.forEach(function(form) {
    // Try to get the current variant data from the form or nearby variant picker
    let variantScript = form.querySelector('script[data-variant]');
    
    // If not found in form, look in the parent container (for variant-picker)
    if (!variantScript) {
      const container = form.closest('.product-form') || form.parentElement;
      if (container) {
        variantScript = container.querySelector('script[data-variant]');
      }
    }
    
    if (variantScript) {
      try {
        const variant = JSON.parse(variantScript.textContent);
        console.log('Found initial variant for form:', variant.id);
        
        // Update backorder flag using the initial variant ID
        updateBackorderFlag(form, variant.id);
      } catch (error) {
        console.error('Error parsing variant data:', error);
      }
    } else {
      console.warn('No variant data found for form');
    }
  });
});